import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useMemo,
} from 'react';
import {useLocation} from 'react-router-dom';
import {ThemeContextType} from '../declarations';

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/* -------------------- System Theme Detection -------------------- */
const getSystemTheme = (): 'digital' | 'print' => {
    if (typeof window === 'undefined') return 'print';

    return window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'digital'
        : 'print';
};

/* -------------------- Theme Provider -------------------- */
export const ThemeProvider: React.FC<{children: React.ReactNode}> = ({
    children,
}) => {
    const {pathname} = useLocation();

    const [systemTheme, setSystemTheme] = useState<'digital' | 'print'>(
        getSystemTheme,
    );

    /* -------------------- System Theme Listener -------------------- */
    useEffect(() => {
        const mq = window.matchMedia('(prefers-color-scheme: dark)');

        const handleChange = () => {
            setSystemTheme(mq.matches ? 'digital' : 'print');
        };

        mq.addEventListener('change', handleChange);

        return () => mq.removeEventListener('change', handleChange);
    }, []);

    /* -------------------- Effective Theme Resolver -------------------- */
    const effectiveTheme = useMemo(() => {
        if (pathname.startsWith('/digital')) return 'digital';
        if (pathname.startsWith('/print')) return 'print';
        return systemTheme;
    }, [pathname, systemTheme]);

    /* -------------------- DOM Theme Sync -------------------- */
    useEffect(() => {
        const root = document.documentElement;

        root.setAttribute('data-theme', effectiveTheme);

        return () => {
            root.removeAttribute('data-theme');
        };
    }, [effectiveTheme]);

    /* -------------------- Context Value -------------------- */
    const value = useMemo<ThemeContextType>(
        () => ({
            theme: effectiveTheme,
            systemTheme,
        }),
        [effectiveTheme, systemTheme],
    );

    return (
        <ThemeContext.Provider value={value}>
            <div className='min-h-screen transition-colors duration-1000'>
                {children}
            </div>
        </ThemeContext.Provider>
    );
};

/* -------------------- Theme Hook -------------------- */
export const useTheme = (): ThemeContextType => {
    const context = useContext(ThemeContext);

    if (!context) {
        throw new Error('useTheme must be used within ThemeProvider');
    }

    return context;
};
